import { ReactNode } from "react";

export interface Tab {
  id: string;
  label: string;
  count?: number;
}

interface TabInterfaceProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  className?: string;
}

/**
 * TabInterface component provides a reusable tab navigation interface
 * with keyboard navigation and accessibility support.
 *
 * Features:
 * - Click and keyboard navigation (Enter, Space)
 * - Black/white theme with active state indicator
 * - Touch-friendly (minimum 44px height)
 * - Full ARIA support for screen readers
 * - Optional badge counts on tabs
 */
export function TabInterface({ tabs, activeTab, onTabChange, className = "" }: TabInterfaceProps): ReactNode {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, tabId: string) => {
    // Support Enter and Space keys for keyboard navigation
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onTabChange(tabId);
    }
  };

  return (
    <div className={`border-b border-border ${className}`} role="tablist" aria-label="Content tabs">
      <div className="flex gap-4 sm:gap-8 overflow-x-auto scrollbar-hide">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTab;

          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.id}`}
              id={`tab-${tab.id}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => onTabChange(tab.id)}
              onKeyDown={(e) => handleKeyDown(e, tab.id)}
              className={`relative -mb-px flex min-h-[44px] flex-shrink-0 items-center gap-2 border-b-2 px-1 py-3 text-sm sm:text-base font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary)] focus:ring-offset-2 ${
                isActive
                  ? "border-[color:var(--color-primary)] text-[color:var(--color-primary)]"
                  : "border-transparent text-textSecondary hover:text-textPrimary"
              } `}
            >
              <span className="whitespace-nowrap">{tab.label}</span>
              {tab.count !== undefined && (
                <span
                  className={`rounded-full px-2 py-0.5 text-xs sm:text-sm ${isActive ? "bg-[color:var(--color-primary)] text-white" : "bg-[color:var(--color-neutral-200)] text-textSecondary"} `}
                  aria-label={`${tab.count} items`}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface TabPanelProps {
  id: string;
  activeTab: string;
  children: ReactNode;
  className?: string;
}

/**
 * TabPanel component wraps tab content with proper ARIA attributes
 */
export function TabPanel({ id, activeTab, children, className = "" }: TabPanelProps): ReactNode {
  const isActive = id === activeTab;

  return (
    <div role="tabpanel" id={`tabpanel-${id}`} aria-labelledby={`tab-${id}`} hidden={!isActive} className={className}>
      {isActive && children}
    </div>
  );
}
